import type { Dispatch, SetStateAction } from "react";
import type { Post } from "../data/models";
import type { PostCardActions } from "../components/PostCard";
import type { MockFeedService } from "../data/mock/feedService";
import { haptic } from "../platform/telegram";

type SetPosts = Dispatch<SetStateAction<Post[]>>;

/**
 * Re-reads mutated posts from the feed store.
 *
 * Spread order matters: some feeds (match feed) hand out copies decorated with
 * view-only fields (`isFromMatchedUser`, `sharedCourse`), so the stored post is
 * spread *over* the copy — fresh interaction state wins, decorations survive.
 */
function refreshed(feed: MockFeedService, posts: Post[]): Post[] {
  return posts.map((p) => {
    const stored = feed.getPost(p.id);
    return stored ? { ...p, ...stored } : p;
  });
}

/**
 * The standard wiring between a list of PostCards and the feed service.
 *
 * Each handler patches local state optimistically, lets the service settle,
 * then re-reads from the store — the store is the truth, the patch is only
 * there so the tap answers under the mock's simulated latency.
 */
export function makePostActions(
  feed: MockFeedService,
  setPosts: SetPosts,
  onOpen?: (post: Post) => void,
): PostCardActions {
  const patch = (id: string, fn: (p: Post) => Post) =>
    setPosts((posts) => posts.map((p) => (p.id === id ? fn(p) : p)));

  const settle = () => setPosts((posts) => refreshed(feed, posts));

  return {
    onOpen,

    onLike(post) {
      const liking = !post.isLikedByMe;
      patch(post.id, (p) => ({
        ...p,
        isLikedByMe: liking,
        likeCount: Math.max(0, p.likeCount + (liking ? 1 : -1)),
      }));
      haptic.tap();
      void (liking ? feed.likePost(post.id) : feed.unlikePost(post.id)).then(settle);
    },

    onRSVP(post) {
      patch(post.id, (p) => ({
        ...p,
        isRSVPd: true,
        attendeeCount: p.attendeeCount !== undefined ? p.attendeeCount + 1 : undefined,
      }));
      haptic.success();
      void feed.rsvpPost(post.id).then(settle);
    },

    onJoin(post) {
      patch(post.id, (p) => ({
        ...p,
        isJoinedByMe: true,
        respondentCount: p.respondentCount !== undefined ? p.respondentCount + 1 : undefined,
      }));
      haptic.success();
      void feed.joinActivity(post.id).then(settle);
    },

    onAcceptRival(post) {
      patch(post.id, (p) => ({ ...p, isOpen: false }));
      haptic.success();
      void feed.acceptRival(post.id).then(settle);
    },

    onDeclineRival(post) {
      // Declining dismisses the challenge — the card leaves the list rather
      // than lingering in a "declined" state nobody acts on.
      setPosts((posts) => posts.filter((p) => p.id !== post.id));
      haptic.tap();
    },

    onApply(post) {
      patch(post.id, (p) => ({ ...p, isAppliedByMe: true }));
      haptic.success();
      void feed.applyPost(post.id).then(settle);
    },
  };
}
